import { QRDataTypeMap } from "@/types/qr";
import type {
  InitDataGetter,
  PreviewComponent,
  PreviewRegistryKey,
  QRDataType,
  QRProps,
} from "../types";
import { logError } from "@/services/DatadogService";

/**
 * Holds preview React components keyed by `data.type`.
 * "register" entries win over "registerLegacy" entries
 * when both exist for the same type.
 */
export class PreviewRegistry {
  private readonly legacy = new Map<PreviewRegistryKey, PreviewComponent>();
  private readonly migrated = new Map<PreviewRegistryKey, PreviewComponent>();
  private readonly initDataGetters = new Map<PreviewRegistryKey, InitDataGetter>();

  /**
   * Registers a migrated component with its specific data loading strategy.
   */
  register(
    type: PreviewRegistryKey,
    component: PreviewComponent,
    getInitData: InitDataGetter
  ): void {
    this.migrated.set(type, component);
    if (getInitData) {
      this.initDataGetters.set(type, getInitData);
    }
  }
  /**
   * Registers legacy components that still depend on the global JSON.
   */
  registerLegacy(type: PreviewRegistryKey, component: PreviewComponent): void {
    this.legacy.set(type, component);
  }

  /**
   * Returns the component prioritizing the migrated version.
   */
  get(type: PreviewRegistryKey): PreviewComponent | undefined {
    return this.migrated.get(type) ?? this.legacy.get(type);
  }

  /**
   * Asynchronously determines the initial data.
   * If the type is migrated, it uses its specific getter.
   * Otherwise, it falls back to the global legacy JSON.
   */
  async getInitialData(type: PreviewRegistryKey): Promise<QRProps<QRDataType> | undefined> {
    try {
      const migratedGetter = this.initDataGetters.get(type);

      if (migratedGetter) {
        return await migratedGetter();
      }

      const importedData = await import("@/data/initial-previews/initPreviewData.json");
      const legacyArray = importedData.default || importedData;
      return legacyArray.find((qr: { type: string }) => qr.type === type) as unknown as QRProps<
        keyof QRDataTypeMap
      >;
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: "PreviewRegistry",
        action: "fetchInitPreviewData",
      });
      return undefined;
    }
  }

  /**
   * Checks if a preview type is registered in either the migrated or legacy maps.
   */
  has(type: PreviewRegistryKey): boolean {
    return this.migrated.has(type) || this.legacy.has(type);
  }

  /**
   * Returns a unique list of all registered preview types (both legacy and migrated).
   */
  getInitDataGetter(type: PreviewRegistryKey): InitDataGetter | undefined {
    return this.initDataGetters.get(type);
  }

  // This is used to get the list of all the preview types that are registered. Foor debugginr or tooling purposes only.
  getRegisteredTypes(): PreviewRegistryKey[] {
    const keys = new Set<PreviewRegistryKey>([...this.legacy.keys(), ...this.migrated.keys()]);
    return [...keys];
  }
}

export const previewRegistry = new PreviewRegistry();
